// src/Home.js
import './Home.css';
import { Link } from 'react-router-dom'; 
import useFetchMenu, { FEATURED_CATEGORY_ID } from './Hooks/UseFetchMenu';
import logo from './Bliss Logo.png';
import { API_BASE_URL } from './config';

const Home = () => {
  const { menuItems, loading } = useFetchMenu(API_BASE_URL);
  const hiddenCategoryIds = [FEATURED_CATEGORY_ID];

  if (loading) {
    return <div></div>;
  }

  const filteredCategories = menuItems.filter(category => !hiddenCategoryIds.includes(category.id));
  
  // Plantillas visuales (fondo, tipo de badge, icono, etc). Antes se
  // asignaban por posicion en la lista (index % 4), lo que hacia que el
  // badge correcto cayera en categorias random segun el orden que devolviera
  // Loyverse (ej. "MIXOLOGY 1o1" en Vino Natural, "SMALL BITES" en
  // Cafeteria). Ahora cada categoria conocida pide explicitamente la
  // plantilla que le corresponde (ver getStyleIndex mas abajo); solo una
  // categoria nueva/no reconocida cae en el ciclo por posicion como fallback.
  const categoryStyles = [
    {
      bgClass: '',
      textClass: 'category-text-white',
      badge: { text: 'SMALL BITES', color: 'bg-fuchsia' },
      ref: 'ALCHEMY',
      icon: 'bolt',
      iconColor: 'category-icon-orange'
    },
    {
      bgClass: 'bg-zinc-950',
      textClass: 'category-text-fuchsia',
      badge: { text: 'DRINKS', color: 'category-badge-orange', vertical: true },
      number: '02',
      hoverClass: 'category-hover-white',
      flexClass: 'flex-end'
    },
    {
      bgClass: '',
      textClass: 'category-text-white',
      badge: { text: 'MIXOLOGY 1o1', border: true },
      scaleClass: 'category-scale-y',
      overlayText: '4U',
      overlayClass: 'text-outline-fuchsia',
      flexClass: 'flex-center'
    },
    {
      bgClass: 'bg-electric-orange',
      textClass: 'category-text-forest',
      translateClass: 'category-translate-x',
    },
    {
      bgClass: 'bg-zinc-950',
      textClass: 'category-text-white',
      hoverClass: 'category-hover-white',
    },
  ];

  // Que plantilla (indice en categoryStyles) le corresponde a cada categoria
  // conocida, por nombre - no por posicion.
  const getStyleIndex = (nameLower, fallbackIndex) => {
    if (nameLower.includes('tapa')) return 0; // SMALL BITES
    if (nameLower.includes('frozen')) return 0; // ICE COOL (ya via badgeText)
    if (nameLower.includes('bebida') || nameLower.includes('zumo') || nameLower.includes('cerveza')) return 0; // JUST DRINKS
    if (nameLower.includes('cafeteria') || nameLower.includes('cafetería')) return 1; // BARISTA
    if (nameLower.includes('vino natural') || nameLower.includes('vinos naturales')) return 1; // PURE JUICE
    if (nameLower.includes('de autor')) return 1; // DRINKS
    if (nameLower.includes('mocktail')) return 3; // Alcohol Free (badge inferior, independiente de la plantilla)
    if (nameLower.includes('copa') && nameLower.includes('vino')) return 4; // JUST POURED (idem)
    if (nameLower.includes('cocktail')) return 2; // MIXOLOGY 1o1 (cae aca solo si no matcheo frozen/mocktail antes)
    return fallbackIndex;
  };

  return (
    <div className="zine-home-container">
      {/* Noise overlay */}
      <div className="noise-overlay"></div>
      
      {/* Grid overlay */}
      <div className="grid-overlay">
        <div className="grid-lines">
          <div className="grid-line"></div>
          <div className="grid-line"></div>
          <div className="grid-line"></div>
        </div>
      </div>

      {/* Scan line animation */}
      <div className="scan-line"></div>

      <main className="zine-main">
        <header className="zine-header">
          <div className="header-top-left">
            <span className="header-badge text-electric-orange">Vol.01</span>
          </div>
          <div className="header-top-right">
            <span className="header-badge text-fuchsia">ISSUE_01</span>
          </div>
          <div className="header-logo-container">
            <Link to="/Home">
              <img src={logo} alt="BLISS LOGO" className="zine-logo" />
            </Link>
          </div>
          <div className="header-bottom">
            <div className="header-info-left">
              <div className="header-info-text">
                Digital Menu<br/>
                <span className="text-fuchsia">Format: Art Zine</span>
              </div>
            </div>
            <div className="header-info-right">
              <div className="header-info-text text-right">
                Est. 2024<br/>
                <span className="text-electric-orange">Location: Void</span>
              </div>
            </div>
          </div>
        </header>

        <nav className="zine-nav">
          {filteredCategories.map((category, index) => {
            const categoryNameLower = category.name.toLowerCase();
            const style = categoryStyles[getStyleIndex(categoryNameLower, index % categoryStyles.length)];
            
            // Cambiar el texto del badge para "tragos frozen" y "BEBIDAS, Zumos, Cervezas"
            const isBebidasZumosCervezas = categoryNameLower.includes('bebida') || 
                                           categoryNameLower.includes('zumo') || 
                                           categoryNameLower.includes('cerveza');
            const badgeText = categoryNameLower.includes('frozen') 
              ? 'ICE COOL' 
              : isBebidasZumosCervezas
              ? 'JUST DRINKS'
              : (style.badge ? style.badge.text : '');
            
            // Cambiar el badge border para "vinos organicos"
            const badgeBorderText = (categoryNameLower.includes('organico') || categoryNameLower.includes('organic'))
              ? 'RAW BALANCE'
              : (style.badge && style.badge.border ? style.badge.text : '');
            
            // Cambiar el overlay text para "vinos organicos"
            const overlayText = (categoryNameLower.includes('organico') || categoryNameLower.includes('organic'))
              ? 'PURE'
              : (style.overlayText || '');
            
            // Cambiar el badge vertical y número para "Vinos Naturales"
            const isVinosNaturales = categoryNameLower.includes('vinos naturales') || categoryNameLower.includes('natural');
            // Cambiar el badge vertical y número para "cafeteria"
            const isCafeteria = categoryNameLower.includes('cafeteria') || categoryNameLower.includes('cafetería');

            // El badge "Alcohol Free" / "Just Poured" se decide por nombre de
            // categoria, no por posicion en el array (categoryStyles rota
            // cada 4 categorias y Loyverse puede devolver el orden distinto,
            // asi que antes el badge caia en categorias random como Cocktails
            // o Copa de vino en vez de Mocktails).
            const isMocktails = categoryNameLower.includes('mocktail');
            const isCopaDeVino = categoryNameLower.includes('copa de vino') ||
                                  categoryNameLower.includes('vinos por copa') ||
                                  categoryNameLower.includes('vino por copa') ||
                                  (categoryNameLower.includes('copa') && categoryNameLower.includes('vino'));
            const bottomBadgeText = isMocktails ? 'Alcohol Free' : isCopaDeVino ? 'JUST POURED' : '';
            
            let badgeVerticalText = '';
            if (isVinosNaturales) {
              badgeVerticalText = 'PURE JUICE';
            } else if (isCafeteria) {
              badgeVerticalText = 'BARISTA';
            } else {
              badgeVerticalText = (style.badge && style.badge.vertical ? style.badge.text : '');
            }
            
            let categoryNumber = '';
            if (isVinosNaturales) {
              categoryNumber = '06';
            } else if (isCafeteria) {
              categoryNumber = '10';
            } else {
              categoryNumber = (style.number || '');
            }
            
            // Aplicar estilo de hover por defecto para cocktails, vinos naturales y cafeteria
            const shouldApplyHoverStyle = categoryNameLower.includes('cocktail') || 
                                         categoryNameLower.includes('vinos naturales') || 
                                         categoryNameLower.includes('natural') ||
                                         categoryNameLower.includes('cafeteria') ||
                                         categoryNameLower.includes('cafetería');
            
            // Aplicar color #e8e8e8 para tapas, vinos organicos, bebidas/zumos/cervezas y tragos frozen
            const shouldApplyCustomGray = categoryNameLower.includes('tapa') ||
                                         (categoryNameLower.includes('organico') || categoryNameLower.includes('organic')) ||
                                         categoryNameLower.includes('bebida') ||
                                         categoryNameLower.includes('zumo') ||
                                         categoryNameLower.includes('cerveza') ||
                                         categoryNameLower.includes('frozen');
            
            let titleClass = '';
            if (shouldApplyHoverStyle) {
              titleClass = `category-title massive-text category-text-hover-default ${style.scaleClass || ''} ${style.translateClass || ''}`;
            } else if (shouldApplyCustomGray) {
              titleClass = `category-title massive-text category-text-custom-gray ${style.hoverClass || ''} ${style.scaleClass || ''} ${style.translateClass || ''}`;
            } else {
              titleClass = `category-title massive-text ${style.textClass} ${style.hoverClass || ''} ${style.scaleClass || ''} ${style.translateClass || ''}`;
            }
            
            return (
              <Link 
                key={category.id} 
                to={`/Category/${category.id}`}
                className={`zine-category-link group ${style.bgClass}`}
              >
                {style.badge && !style.badge.vertical && !style.badge.border && (
                  <div className="category-badge-top">
                    <span className={`category-badge ${style.badge.color}`}>
                      {badgeText}
                    </span>
                  </div>
                )}
                {style.badge && style.badge.border && (
                  <span className={`category-badge category-badge-border`}>
                    {badgeBorderText}
                  </span>
                )}
                {style.badge && style.badge.vertical && (
                  <div className={`category-badge-vertical ${(isVinosNaturales || isCafeteria) ? 'category-badge-vertical-raised' : ''}`}>
                    <span className={`category-badge-vertical-text ${style.badge.color}`}>
                      {badgeVerticalText}
                    </span>
                  </div>
                )}
                <div className={`category-content ${style.flexClass || ''}`}>
                  <h2 className={titleClass}>
                    {category.name.split(' ').map((word, i) => (
                      <span key={i}>
                        {word}
                        {i < category.name.split(' ').length - 1 && <br/>}
                      </span>
                    ))}
                  </h2>
                  {style.ref && (
                    <span className="category-ref">{style.ref}</span>
                  )}
                  {categoryNumber && (
                    <div className="category-number">{categoryNumber}</div>
                  )}
                  {overlayText && (
                    <span className={`category-overlay-text ${style.overlayClass}`}>{overlayText}</span>
                  )}
                  {bottomBadgeText && (
                    <div className="category-bottom-badge category-bottom-badge-pill">
                      <span className="category-bottom-text">
                        {bottomBadgeText}
                      </span>
                      <div className="category-pulse-bar"></div>
                    </div>
                  )}
                </div>
                {style.icon && (
                  <div className="category-icon-container">
                    <span className={`material-symbols-outlined category-icon ${style.iconColor}`}>
                      {style.icon}
                    </span>
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        <footer className="zine-footer">
          <div className="footer-grid">
            <div className="footer-left">
              <div className="footer-section">
                <span className="footer-label">Protocol</span>
                <span className="footer-value">STRICTLY TAG US @BLISS.RUZAFA ;)</span>
              </div>
              <div className="footer-section">
                <span className="footer-label">Interface</span>
                <span className="footer-value">V.01-MOBILE_CORE</span>
              </div>
            </div>
            <div className="footer-right">
              <div className="footer-qr">
                <span className="material-symbols-outlined qr-icon">qr_code_2</span>
              </div>
              <span className="footer-copyright">
                This is BLISS digital publication. All rights reserved by BLISS Labs.
              </span>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="footer-by">BY BLISS / 2024</span>
            <Link to="/AudioCulture" className="footer-button">
              Enter Void
            </Link>
          </div>
        </footer>

        {/* Floating text */}
        <div className="floating-text">
          <span className="floating-text-content">REINA NA MARIA 1, VALENCIA</span>
        </div>
      </main>
    </div>
  );
};

export default Home;
